import * as React from 'react';
import { getResident, type GetResidentResponse } from '@/api/residents';
import { Box, Stack, Typography, Container, useTheme, Button } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react/dist/ssr';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Seo } from '@/components/seo';
import { UserHeader } from '../components/user-header';
import { CommunityAndUnitDetails } from './components/community-and-unit-details';
import { ResidentCustomFields } from './components/resident-custom-fields';
import { ResidentDetails } from './components/user-details';

export function ResidentPage(): React.JSX.Element {
  const { community } = useUser();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data, isLoading, error } = useQuery<GetResidentResponse | null>({
    queryKey: ['residents', community?.id, userId ?? ''],
    queryFn: async () => {
      const res = await getResident(community?.id, userId ?? '', {
        include: 'units,units.unitType,communityRoles',
      });
      return res;
    },
    retry: false,
  });

  const resident = data?.resident ?? null;
  const customFields = data?.custom_fields ?? [];
  const customFieldValues = data?.custom_field_values ?? {};
  const { user: currentUser } = useUser();
  const isManager = true;
  const isViewingSelf = currentUser?.id === resident?.id;

  if (resident?.units && resident.units.length > 0) {
    resident.units.sort((a, b) => {
      const moveOutDateA = a.pivot.move_out_date ? new Date(a.pivot.move_out_date).getTime() : 0;
      const moveOutDateB = b.pivot.move_out_date ? new Date(b.pivot.move_out_date).getTime() : 0;
      return moveOutDateA - moveOutDateB;
    });
  }

  // Show user not found error
  if (!isLoading && error?.message === 'User not found') {
    return (
      <>
        <Seo title="User Not Found" />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: theme.palette.background.default,
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="xl">
            <CommunityPageHeader
              breadcrumbs={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Users',
                  href: paths.users.index,
                },
                {
                  label: 'User Not Found',
                },
              ]}
              icon={<UsersThreeIcon size={36} />}
              subtitle="The user you're looking for doesn't exist or has been removed"
              title="User Not Found"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  color: 'primary.main',
                  opacity: 0.8,
                  mb: 4,
                }}
              >
                <UsersThreeIcon size={200} />
              </Box>

              <Typography color="text.secondary" variant="body1" sx={{ mb: 2 }}>
                User ID: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{userId}</code>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                  sx={{ minWidth: 120 }}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  href={paths.users.index}
                  sx={{ minWidth: 120 }}
                >
                  View All Users
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="User" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            breadcrumbs={[
              {
                label: 'Home',
                href: '/',
              },
              {
                label: 'Users',
                href: paths.users.index,
              },
              {
                label: resident?.first_name && resident?.last_name
                  ? `${resident.first_name} ${resident.last_name}`
                  : 'User Details',
              },
            ]}
            icon={<UsersThreeIcon size={36} />}
            subtitle="View user details, roles, and unit assignments"
            title={resident?.first_name && resident?.last_name
              ? `${resident.first_name} ${resident.last_name}`
              : 'Loading User...'
            }
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ position: 'relative' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Grid container spacing={4}>
                <Grid lg={4} xs={12}>
                  <Stack spacing={4}>
                    <ResidentDetails error={error} isLoading={isLoading} resident={resident} />
                    <ResidentCustomFields
                      communityId={community!.id}
                      residentId={resident?.id ?? ''}
                      customFields={customFields}
                      customFieldValues={customFieldValues}
                      isManager={isManager}
                      isViewingSelf={isViewingSelf}
                      queryKey={['residents', community?.id, userId ?? '']}
                    />
                  </Stack>
                </Grid>
                <Grid lg={8} xs={12}>
                  <CommunityAndUnitDetails
                    community={community!}
                    communityRoles={resident?.community_roles || []}
                    communityTitle={resident?.community_roles?.[0]?.pivot?.user_title_id || null}
                    loading={isLoading}
                    units={resident?.units || []}
                  />
                </Grid>
                {/* <Grid lg={4} xs={12}>
                  <DeleteResidentCard />
                </Grid> */}
              </Grid>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
